// eslint-disable-next-line lumapps/do-not-import-classnames
import classnames from 'classnames';
import { ClassValue } from 'classnames/types';
import isArray from 'lodash/isArray';
import isObject from 'lodash/isObject';
import isString from 'lodash/isString';
import mapKeys from 'lodash/mapKeys';

import type { Size } from '@lumapps/lumx/react';

export * from '@lumapps/lumx/core/utils/classNames';

/**
 * Returns the classname associated to the given size. For example, for Size.xs it returns
 * lumx-size-xs
 * @param size - Size
 * @returns string
 */
export const sizing = (size: Size) => {
    return `lumx-size-${size}`;
};

/**
 * Returns the classname in order to set the "required" style on a label / string.
 * @returns string
 */
export const required = () => {
    return 'required';
};

/**
 * Returns the classname in order to add a separator to an element
 * @returns string
 */
export const separator = () => {
    return 'lumx-separator';
};

type Modifier = string | Record<string, boolean> | ClassValue[];

const parseModifier = (baseClass: string, modifier: Modifier) => {
    const classes = [];

    if (isString(modifier)) {
        classes.push(`${baseClass}--${modifier}`);
    } else if (isArray(modifier)) {
        return modifier;
    } else if (isObject(modifier)) {
        classes.push(
            mapKeys(modifier, (_value, key) => {
                return `${baseClass}--${key}`;
            }),
        );
    }

    return classes;
};

const generateBEMClass = (
    baseClass: string,
    modifier?: Modifier,
    additionalClasses?: ClassValue | ClassValue[],
    modifierLast?: boolean,
) => {
    if (!modifier && !additionalClasses) {
        return baseClass;
    }

    if (modifierLast) {
        return classnames(baseClass, additionalClasses, modifier && parseModifier(baseClass, modifier));
    }

    return classnames(baseClass, modifier && parseModifier(baseClass, modifier), additionalClasses);
};

/**
 * Returns the BEM class for the given block (and modifier if it is defined)
 * @param baseClass - string
 * @returns string
 */
export const block =
    (baseClass: string) =>
    (modifier?: Modifier, additionalClasses?: ClassValue | ClassValue[], modifierLast?: boolean) => {
        return generateBEMClass(baseClass, modifier, additionalClasses, modifierLast);
    };

/**
 * Returns the BEM class for the given element (and modifier if it is defined)
 * @param baseClass - string
 * @returns string
 */
export const element =
    (baseClass: string) =>
    (elem: string, modifier?: Modifier, additionalClasses?: ClassValue | ClassValue[], modifierLast?: boolean) => {
        const elementClassname = `${baseClass}__${elem}`;

        return generateBEMClass(elementClassname, modifier, additionalClasses, modifierLast);
    };
